import type { IExerciseRecord, IPRRecord, ITrainingRecord } from '@/hooks/useAppState';
import type { IExercise } from '@/data/exercises';

export interface IShareExerciseItem {
  name: string;
  sets: number;
  reps: string;
  weight: number;
  repsUnit?: '次' | '秒' | '分钟';
  setReps?: number[];
}
export interface WorkoutShareCardData {
  durationMin: number;
  totalVolume: number;
  totalSets: number;
  totalWeight: number;
  exerciseCount: number;
  streakDays: number;
  newPRs: IPRRecord[];
  dayName?: string;
  exerciseList: IShareExerciseItem[];
  date?: string;
  completed?: boolean;
}

const positive = (n: number) => Number.isFinite(n) && n > 0;
const cleanNumber = (n: number) => Number.isFinite(n) && n >= 0 ? n : 0;
export function canShareWorkoutCompletion(exercises: IExerciseRecord[]) {
  return exercises.some(exercise => exercise.sets.some(s => s.completed && positive(s.reps) && Number.isFinite(s.weight) && s.weight >= 0));
}
export function localShareDate(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Read performed sets only, keeping the weight and repetitions from the same best set. */
export function buildWorkoutShareData(options: {
  exercises: IExerciseRecord[];
  durationMin: number;
  getExercise: (id: string) => IExercise | undefined;
  newPRs?: IPRRecord[];
  streakDays?: number;
  date?: string;
  dayName?: string;
  completed?: boolean;
}): WorkoutShareCardData {
  const performed = options.exercises.map(exercise => ({ ...exercise,
    sets: exercise.sets.filter(s => s.completed && positive(s.reps) && Number.isFinite(s.weight) && s.weight >= 0),
  })).filter(exercise => exercise.sets.length > 0);
  const sets = performed.flatMap(exercise => exercise.sets);
  const exerciseList = performed.flatMap(record => {
    const exercise = options.getExercise(record.exerciseId);
    if (exercise?.isWarmup || exercise?.isStretch) return [];
    const best = record.sets.reduce((a, b) => b.weight > a.weight || (b.weight === a.weight && b.reps > a.reps) ? b : a);
    const repsUnit = exercise?.reps.includes('分钟') ? '分钟' : exercise?.reps.includes('秒') ? '秒' : '次';
    return [{ name: exercise?.name || record.exerciseName, sets: record.sets.length,
      reps: String(best.reps), weight: best.weight, repsUnit, setReps: record.sets.map(s => s.reps) } satisfies IShareExerciseItem];
  });
  return {
    durationMin: Math.round(cleanNumber(options.durationMin)), totalSets: sets.length,
    exerciseCount: performed.length, totalWeight: sets.reduce((sum, s) => sum + s.weight, 0),
    totalVolume: sets.reduce((sum, s) => sum + s.weight * s.reps, 0), exerciseList,
    newPRs: options.newPRs || [], streakDays: options.streakDays || 0,
    date: options.date || localShareDate(), dayName: options.dayName, completed: options.completed,
  };
}

export function latestShareRecord(records: ITrainingRecord[]) {
  return [...records].reverse().filter(r => r.exercises.some(e => e.sets.some(s => s.completed && positive(s.reps))))
    .sort((a, b) => b.date.localeCompare(a.date))[0];
}

export function createPosterModel(data?: WorkoutShareCardData) {
  const hasWorkout = !!data && data.totalSets > 0;
  const pr = hasWorkout ? [...data.newPRs].filter(p => positive(p.maxWeight) && positive(p.maxReps))
    .sort((a, b) => b.maxWeight - a.maxWeight)[0] : undefined;
  const best = hasWorkout ? [...data.exerciseList].filter(e => e.sets > 0 && positive(e.weight))
    .sort((a, b) => b.weight - a.weight)[0] : undefined;
  const date = data?.date || localShareDate();
  const value = pr ? String(pr.maxWeight) : best ? String(best.weight) : hasWorkout ? '自重' : '练刻';
  const exerciseLine = pr ? `${pr.exerciseName} · ${pr.maxReps} 次`
    : best ? `${best.name} · ${best.reps} ${best.repsUnit || '次'}`
    : hasWorkout ? data.dayName || '本次训练' : '动作库 · 训练计划 · 饮食记录';
  return {
    date: date.replaceAll('-', '.'), fileName: `练刻_${hasWorkout ? '训练分享' : '邀请朋友'}_${date}.png`,
    headline: pr ? '又一次，突破自己' : hasWorkout ? '每一次，都向前一步' : '练出改变，刻下坚持',
    badge: pr ? '刷新个人纪录' : hasWorkout ? data.completed === false ? '已记录本次训练' : '本次训练已完成' : '从今天开始',
    value, unit: pr || best ? 'kg' : '', exerciseLine: exerciseLine.length > 22 ? exerciseLine.slice(0, 21) + '…' : exerciseLine,
    caption: hasWorkout ? '每一点进步，都值得记录' : '和朋友一起，练出更好的自己',
    metrics: hasWorkout ? [
      { value: String(Math.round(cleanNumber(data.durationMin))), unit: '分钟' },
      { value: String(Math.round(cleanNumber(data.exerciseCount))), unit: '个动作' },
      { value: String(Math.round(cleanNumber(data.totalSets))), unit: '组' },
    ] : [{ value: '动作库', unit: '' }, { value: '训练计划', unit: '' }, { value: '饮食记录', unit: '' }],
  };
}
export type SharePosterModel = ReturnType<typeof createPosterModel>;
